import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiCheckSquare, FiSquare, FiX } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";

function getBusinessUnitId(searchParams) {
  const user = readStoredUser();
  return searchParams.get("business_unit_id") || user?.active_business_unit_id || user?.business_unit_ids?.[0] || "1";
}

function currentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function formatThousands(value) {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "0";
  const rounded = Math.round(number);
  const sign = rounded < 0 ? "-" : "";
  return `${sign}${String(Math.abs(rounded)).replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
}

function formatMoney(value) {
  return `${formatThousands(value)} €`;
}

function compactMoney(value) {
  const number = Number(value || 0);
  if (!number) return "-";
  return formatThousands(number);
}

function familyKey(family) {
  return String(family?.id || "");
}

export default function SalesByFamily() {
  const [searchParams] = useSearchParams();
  const businessUnitId = getBusinessUnitId(searchParams);
  const [month, setMonth] = useState(currentMonth);
  const [selectedFamilies, setSelectedFamilies] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selectedFamilySet = useMemo(() => new Set(selectedFamilies), [selectedFamilies]);
  const familyIdsParam = selectedFamilies.join(",");
  const days = report?.days || [];
  const rows = report?.rows || [];
  const families = report?.families || [];
  const totals = report?.totals || {};
  const totalsByDay = report?.totals_by_day || {};
  const missingVatLines = Number(report?.warnings?.missing_vat_lines || 0);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get("/api/analytics/sales/net-by-family-day", {
        params: {
          business_unit_id: businessUnitId,
          month,
          family_ids: familyIdsParam,
        },
      });
      setReport(res.data || null);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not load sales analysis.");
    } finally {
      setLoading(false);
    }
  }, [businessUnitId, familyIdsParam, month]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const toggleFamily = (key) => {
    setSelectedFamilies((current) => {
      if (current.includes(key)) return current.filter((item) => item !== key);
      return [...current, key];
    });
  };

  const selectAllFamilies = () => {
    setSelectedFamilies([]);
  };

  const clearFamilies = () => {
    setSelectedFamilies([]);
  };

  const visibleFamilyLabel =
    selectedFamilies.length === 0 ? "All families" : `${selectedFamilies.length} families`;

  return (
    <div className="reservations-container rm-page analyticsSalesPage">
      <div className="rm-toolbarCard">
        <div className="rm-toolbarRow">
          <div>
            <h3 className="rm-rates-title">Net sales by family</h3>
            <div className="rm-rates-subtitle">
              {loading ? "Loading analysis..." : `${visibleFamilyLabel} / ${month}`}
            </div>
          </div>

          <div className="rm-topActions">
            <input
              className="posTextInput analyticsMonthInput"
              type="month"
              value={month}
              onChange={(event) => setMonth(event.target.value || currentMonth())}
            />
            <button type="button" className="rm-topBtn rm-topBtn--violet" onClick={selectAllFamilies}>
              <FiCheckSquare /> All families
            </button>
            <button type="button" className="rm-topBtn" onClick={clearFamilies} disabled={!selectedFamilies.length}>
              <FiX /> Clear
            </button>
          </div>
        </div>
      </div>

      {error ? <div className="posStateBanner">{error}</div> : null}
      {missingVatLines ? (
        <div className="posStateBanner analyticsWarningBanner">
          {formatThousands(missingVatLines)} sales lines have products without IVA. Net sales are incomplete for those
          lines.
        </div>
      ) : null}

      <div className="posMetricGrid analyticsMetricGrid">
        <div className="posMetricTile">
          <span>Net sales</span>
          <strong>{formatMoney(totals.net_sales)}</strong>
        </div>
        <div className="posMetricTile">
          <span>PVP sales</span>
          <strong>{formatMoney(totals.gross_sales)}</strong>
        </div>
        <div className="posMetricTile">
          <span>Lines</span>
          <strong>{formatThousands(totals.line_count)}</strong>
        </div>
        <div className="posMetricTile">
          <span>IVA pending lines</span>
          <strong>{formatThousands(missingVatLines)}</strong>
        </div>
      </div>

      <div className="rm-rates-card analyticsFamilyCard">
        <div className="rm-rates-header analyticsCompactHeader">
          <h3 className="rm-rates-title">Families</h3>
          <div className="rm-rates-subtitle">Rows included in the matrix</div>
        </div>
        <div className="analyticsFamilyGrid">
          {families.map((family) => {
            const key = familyKey(family);
            const selected = selectedFamilies.length === 0 || selectedFamilySet.has(key);
            return (
              <button
                type="button"
                key={key}
                className={`analyticsFamilyChip ${selected ? "analyticsFamilyChip--selected" : ""}`}
                onClick={() => toggleFamily(key)}
              >
                {selected ? <FiCheckSquare /> : <FiSquare />}
                <span>{family.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rm-rates-card analyticsMatrixCard">
        <div className="rm-rates-header">
          <h3 className="rm-rates-title">Daily net sales matrix</h3>
          <div className="rm-rates-subtitle">Columns are days of the selected month</div>
        </div>

        <div className="res-listMetaRow rm-listMetaRow">
          <small className="res-listMetaText">
            {loading ? "Loading matrix..." : `${rows.length} family rows`}
          </small>
        </div>

        <div className="rm-rates-table-wrap analyticsMatrixWrap">
          <table
            className="rm-rates-table analyticsMatrixTable"
            style={{
              "--rm-col1-width": "220px",
              "--rm-table-min-width": `${Math.max(980, 310 + days.length * 78)}px`,
            }}
          >
            <colgroup>
              <col style={{ width: "220px" }} />
              {days.map((day) => (
                <col key={day.date} style={{ width: "78px" }} />
              ))}
              <col style={{ width: "120px" }} />
            </colgroup>
            <thead>
              <tr>
                <th className="res-th rm-sticky-1">
                  <div className="res-thBtn">
                    <span className="res-thLabel">Family</span>
                    <span className="res-thSub">POS taxonomy</span>
                  </div>
                </th>
                {days.map((day) => (
                  <th key={day.date} className="res-th">
                    <div className="res-thBtn analyticsDayHeader">
                      <span className="res-thLabel">{day.label}</span>
                      <span className="res-thSub">{month}</span>
                    </div>
                  </th>
                ))}
                <th className="res-th">
                  <div className="res-thBtn">
                    <span className="res-thLabel">Total</span>
                    <span className="res-thSub">Month</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.family_key}>
                  <td className="rm-sticky-1 analyticsFamilyName">
                    <strong>{row.family_name}</strong>
                    <span>{row.family_code}</span>
                  </td>
                  {days.map((day) => {
                    const cell = row.days?.[day.date] || {};
                    const hasMissingVat = Number(cell.missing_vat_lines || 0) > 0;
                    return (
                      <td
                        key={day.date}
                        className={`analyticsMoneyCell ${hasMissingVat ? "analyticsMoneyCell--warning" : ""}`}
                        title={hasMissingVat ? `${cell.missing_vat_lines} lines without IVA` : undefined}
                      >
                        {compactMoney(cell.net_sales)}
                      </td>
                    );
                  })}
                  <td className="analyticsMoneyCell analyticsMoneyCell--total">
                    {compactMoney(row.totals?.net_sales)}
                  </td>
                </tr>
              ))}
              {!rows.length ? (
                <tr>
                  <td colSpan={days.length + 2} className="posMuted">
                    No net sales found for this selection.
                  </td>
                </tr>
              ) : null}
              {rows.length ? (
                <tr className="analyticsTotalRow">
                  <td className="rm-sticky-1 analyticsFamilyName">
                    <strong>Total</strong>
                    <span>Net sales</span>
                  </td>
                  {days.map((day) => (
                    <td key={day.date} className="analyticsMoneyCell analyticsMoneyCell--total">
                      {compactMoney(totalsByDay[day.date]?.net_sales)}
                    </td>
                  ))}
                  <td className="analyticsMoneyCell analyticsMoneyCell--total">
                    {compactMoney(totals.net_sales)}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
